import type { Task } from '@/common/api/models/task';
import type { EventKind } from '@/common/api/models/eventKind';
import { isLecturerScoredTask } from '@/common/utils/taskEvaluationUtils';
import { isOralExamTask } from './evaluationFormMapping';
import type { TaskFormEvaluation } from '../types';

export interface MissingEvalRatingGap {
  taskId: string;
  taskTitle: string;
  missingDimensions: string[];
}

function isMissingLevel(level: string | null | undefined): boolean {
  return !level || level === 'not_applicable';
}

/**
 * Collect flat-task scored-dimension gaps for Save & Submit (draft save stays permissive).
 * Activity tasks are skipped (mark-as-done only). Oral-exam completeness is deferred.
 * When ``evalDimensionKeys`` is passed, ignore scored ids the tenant/snapshot
 * does not present (e.g. leftover ``formal`` on a task).
 */
export function collectMissingEvalRatings(
  tasks: Task[],
  taskEvaluations: Record<string, TaskFormEvaluation>,
  eventKinds: EventKind[],
  evalDimensionKeys?: string[],
): MissingEvalRatingGap[] {
  const gaps: MissingEvalRatingGap[] = [];

  tasks.forEach((task) => {
    if (!task.id || !isLecturerScoredTask(task, eventKinds) || isOralExamTask(task)) {
      return;
    }

    const scoredIds = (task.scoredDimensionIds ?? []).filter(
      (dim) => !evalDimensionKeys || evalDimensionKeys.includes(dim),
    );
    if (scoredIds.length === 0) {
      return;
    }

    const formEval = taskEvaluations[task.id];
    if (!formEval || formEval.kind !== 'flat') {
      gaps.push({
        taskId: task.id,
        taskTitle: task.title ?? task.id,
        missingDimensions: [...scoredIds],
      });
      return;
    }

    const missingDimensions = scoredIds.filter((dim) => {
      const level = formEval.evaluation?.dimensions?.[dim]?.level;
      return isMissingLevel(level);
    });

    if (missingDimensions.length > 0) {
      gaps.push({
        taskId: task.id,
        taskTitle: task.title ?? task.id,
        missingDimensions,
      });
    }
  });

  return gaps;
}
